<script lang="ts">
	import type { HolsterAccessoryToSelectedAttributes, AllHolsterAccessoryAttributes } from 'shared/bzns/holster/accessories'
	import type { Writable } from 'svelte/store'
	import AccessorySelectorLine, { type Accessory } from './AccessorySelectorLine.svelte'
	import { slide } from 'svelte/transition'

	import { createEventDispatcher } from 'svelte'
	const dispatch = createEventDispatcher()

	import { calculate_holster_or_accessory_final_price } from 'shared/bzns/holster/holster_pricing'
	import { format_money_$_with_optional_cents } from 'shared/format_currency'
	import { financial_sum, zero } from 'shared/number'

	export let accessories: Accessory[]
	export let accessories_with_purchasability: Accessory[]
	export let holster_line_image_file_name_string: TrexHolsterLine[`image_file_name_string`]
	export let holster_line_image_position_algorithm: TrexHolsterLine[`image_position_algorithm`]
	export let promo_active: boolean
	export let checked: boolean
	export let holster_accessory_ref_to_color_ref: Map<number, number>
	export let holster_accessory_to_attributes: { [holster_accessory_ref: number]: AllHolsterAccessoryAttributes }
	export let holster_accessory_to_selected_attributes: HolsterAccessoryToSelectedAttributes
	export let selected_holster_properties: Writable<{ [key: string]: unknown }>

	$: color_ref_to_additional_price = new Map(accessories[0].holster_accessory_colors.map(hac => [ hac.color_ref, hac.additional_price ]))
	$: holster_accessory_ref_to_color_additional_price = new Map(Array.from(holster_accessory_ref_to_color_ref.entries())
		.map(([ holster_accessory_ref, color_ref ]) => [ holster_accessory_ref, color_ref_to_additional_price.get(color_ref) ])
	)

	$: base_price = financial_sum(accessories.map(accessory => calculate_holster_or_accessory_final_price({
		base_price: accessory.base_price,
		promo_discount_rate: accessory.promo_discount_rate,
		promo_discount_usd: accessory.promo_discount_usd,
		promo_active: false,
		color_additional_price: holster_accessory_ref_to_color_additional_price.get(accessory.holster_accessory_ref) || zero,
	})))

	$: price = financial_sum(accessories.map(accessory => calculate_holster_or_accessory_final_price({
		base_price: accessory.base_price,
		promo_discount_rate: accessory.promo_discount_rate,
		promo_discount_usd: accessory.promo_discount_usd,
		promo_active,
		color_additional_price: holster_accessory_ref_to_color_additional_price.get(accessory.holster_accessory_ref) || zero,
	})))

</script>

<div class=accessory_group transition:slide={{ delay: 0, duration: 250 }}>
	<div class=accessories>
		{#each accessories as accessory (accessory.holster_accessory_variation_ref)}
			<AccessorySelectorLine
				{accessories_with_purchasability}
				{accessory}
				attributes={holster_accessory_to_attributes[accessory.holster_accessory_ref]}
				bind:selected_attributes={holster_accessory_to_selected_attributes[accessory.holster_accessory_ref]}
				bind:holster_accessory_ref_to_color_ref
				{holster_line_image_file_name_string}
				{holster_line_image_position_algorithm}
				{promo_active}
				{selected_holster_properties}
				{checked}
			></AccessorySelectorLine>
		{/each}
	</div>

	<label class="include">
		{#if !accessories.every(accessory => accessory.include_one_with_holster)}
			{@const purchasable = accessories.every(accessory => accessory.purchasable)}
			<div class="include_text">
				{#if purchasable}
					INCLUDE
				{/if}
			</div>
			<div class="include_checkbox">
				{#if purchasable}
					<input
						type="checkbox"
						on:change={() => dispatch(`toggle`, accessories)}
						{checked}
					/>
				{/if}
				<div class=price>
					{#if promo_active && !base_price.equal(price)}
						<del style="color: var(--disabled-gray)">+{format_money_$_with_optional_cents(base_price)}</del>
					{/if}
					+{format_money_$_with_optional_cents(price)}
				</div>
			</div>
		{:else}
			INCLUDED
		{/if}
	</label>
</div>

<style>
	.accessory_group {
		display: flex;
		flex-direction: row;
		gap: 8px;
		padding: 8px 8px 0 8px; /* to let the accessories represent the spacing with padding, which can be lerped for animations */
		background-color: #111; /* It's helpful for the color picker popover to have a tiny bit of contrast with the background */
		justify-content: space-between;
	}

	.include {
		display: flex;
		gap: 4px;
		flex-direction: column;
		align-items: flex-start;
		align-self: center;
		padding-right: 1em;
		font-size: 1em;
		font-weight: 500;
		color: var(--contrast-gray);
	}

	.include_text {
		padding-right: 0.5rem;
	}

	.include_checkbox {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 4px;
	}

	input[type=checkbox] {
		width: 1em;
		height: 1em;
		font-size: 2em;
	}

	.price {
		display: flex;
		align-items: end;
		flex-direction: column;
		font-variant-numeric: tabular-nums;
		white-space: nowrap;
		color: white;
	}

	@media(max-width: 420px) {
		.include {
			padding-right: 0;
		}
		.include_text {
			display: none;
		}
		.include_checkbox {
			flex-direction: column;
			align-items: flex-start;
		}
	}
</style>
